/**
 * FacilitySleepPage — Facility-Level Sleep Overview
 *
 * Layout: Thin summary panel on the left (total residents + severity filters),
 * 4 metric cards on the right (Sleep Time, Efficiency, WASO, Wake Episodes).
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import apiService from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import "../styles/pages/FacilitySleepPage.css";

/* ── Constants ── */

const ROW_HEIGHT = 44; // row padding + content + gap

/* ── Icons ── */

const SleepTimeIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const EfficiencyIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const WasoIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const WakeEpisodesIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z" />
    <path d="M12 6v6" />
    <path d="M9 15h6" />
  </svg>
);

const PeopleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/* ── Subcomponents ── */

function ResidentRow({ resident, onClick }) {
  const handleActivate = useCallback(() => onClick(resident.id), [onClick, resident.id]);
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(resident.id);
    }
  }, [onClick, resident.id]);

  return (
    <div
      className="facility-sleep-resident-row"
      onClick={handleActivate}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="facility-sleep-resident-row__left">
        <span className="facility-sleep-resident-row__name">{resident.name}</span>
        <span className="facility-sleep-resident-row__room">
          {resident.room ? `Room ${resident.room}` : ""}
        </span>
      </div>
      <div className="facility-sleep-resident-row__right">
        <span className="facility-sleep-resident-row__value">
          {resident.todayValue} {resident.unit}
        </span>
        <span className={`facility-sleep-resident-row__decline facility-sleep-resident-row__decline--${resident.severity}`}>
          ↓ {resident.percentDecline}%
        </span>
        <span className={`facility-sleep-resident-row__badge facility-sleep-resident-row__badge--${resident.severity}`} />
      </div>
    </div>
  );
}

function MetricCard({ title, icon: Icon, residents, onResidentClick }) {
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const listRef = useRef(null);

  // Use ResizeObserver for reliable height measurement
  useEffect(() => {
    const node = listRef.current;
    if (!node) return;

    const calculateItems = () => {
      const listHeight = node.clientHeight;
      if (listHeight > 0) {
        setItemsPerPage(Math.max(1, Math.floor(listHeight / ROW_HEIGHT)));
      }
    };

    const observer = new ResizeObserver(calculateItems);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Reset page when residents list changes
  useEffect(() => { setPage(0); }, [residents]);

  const totalPages = Math.ceil(residents.length / itemsPerPage);
  const startIdx = page * itemsPerPage;
  const visibleResidents = residents.slice(startIdx, startIdx + itemsPerPage);

  return (
    <div className="facility-sleep-card">
      <div className="facility-sleep-card__header">
        <div className="facility-sleep-card__header-left">
          <div className="facility-sleep-card__icon"><Icon /></div>
          <span className="facility-sleep-card__title">{title}</span>
        </div>
        <span className="facility-sleep-card__count">{residents.length} flagged</span>
      </div>
      <div className="facility-sleep-card__body">
        {residents.length === 0 ? (
          <div className="facility-sleep-card__empty">
            <span className="facility-sleep-card__empty-text">All within normal range ✓</span>
          </div>
        ) : (
          <>
            <div className="facility-sleep-card__list" ref={listRef}>
              {visibleResidents.map((r) => (
                <ResidentRow key={r.id} resident={r} onClick={onResidentClick} />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="facility-sleep-card__pagination">
                <button
                  className="facility-sleep-card__pagination-btn"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  aria-label="Previous page"
                >
                  <svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <span className="facility-sleep-card__pagination-info">{page + 1} / {totalPages}</span>
                <button
                  className="facility-sleep-card__pagination-btn"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  aria-label="Next page"
                >
                  <svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Main Component ── */

export default function FacilitySleepPage({ onResidentClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getFacilitySleepOverview();
      setData(result);
    } catch (err) {
      console.error("[FacilitySleepPage] Failed to load overview:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFilterClick = useCallback((severity) => {
    setActiveFilter((current) => (current === severity ? null : severity));
  }, []);

  const handleResidentClick = useCallback((residentId) => {
    if (onResidentClick) onResidentClick(residentId);
  }, [onResidentClick]);

  // Memoize derived data
  const sleepTimeAll = useMemo(() => data?.sleep_time || [], [data]);
  const efficiencyAll = useMemo(() => data?.efficiency || [], [data]);
  const wasoAll = useMemo(() => data?.waso || [], [data]);
  const wakeEpisodesAll = useMemo(() => data?.wake_episodes || [], [data]);

  const { counts, totalResidentsShown } = useMemo(() => {
    const allEntries = [...sleepTimeAll, ...efficiencyAll, ...wasoAll, ...wakeEpisodesAll];
    const uniqueIds = new Set(allEntries.map((r) => r.id));
    return {
      counts: {
        attention: allEntries.filter((r) => r.severity === "attention").length,
        monitor: allEntries.filter((r) => r.severity === "monitor").length,
        observe: allEntries.filter((r) => r.severity === "observe").length,
      },
      totalResidentsShown: uniqueIds.size,
    };
  }, [sleepTimeAll, efficiencyAll, wasoAll, wakeEpisodesAll]);

  // Memoize filtered arrays
  const filteredSleepTime = useMemo(() => activeFilter ? sleepTimeAll.filter((r) => r.severity === activeFilter) : sleepTimeAll, [sleepTimeAll, activeFilter]);
  const filteredEfficiency = useMemo(() => activeFilter ? efficiencyAll.filter((r) => r.severity === activeFilter) : efficiencyAll, [efficiencyAll, activeFilter]);
  const filteredWaso = useMemo(() => activeFilter ? wasoAll.filter((r) => r.severity === activeFilter) : wasoAll, [wasoAll, activeFilter]);
  const filteredWakeEpisodes = useMemo(() => activeFilter ? wakeEpisodesAll.filter((r) => r.severity === activeFilter) : wakeEpisodesAll, [wakeEpisodesAll, activeFilter]);

  if (loading) {
    return (
      <main className="facility-sleep-page--loading">
        <LoadingSpinner message="Loading sleep overview..." />
      </main>
    );
  }

  if (error && !data) {
    return (
      <main className="facility-sleep-page--error">
        <div className="facility-sleep-card__error">
          <span className="facility-sleep-card__error-text">Failed to load facility sleep overview</span>
          <button className="facility-sleep-card__error-retry" onClick={loadData}>Retry</button>
        </div>
      </main>
    );
  }

  return (
    <main className="facility-sleep-page">
      {/* ── Left Summary Panel ── */}
      <div className="facility-sleep-summary-panel">
        <div className="facility-sleep-summary-panel__card">
          <div className="facility-sleep-summary-panel__card-icon">
            <PeopleIcon />
          </div>
          <span className="facility-sleep-summary-panel__card-value">{totalResidentsShown}</span>
          <span className="facility-sleep-summary-panel__card-label">Residents Flagged</span>
        </div>

        <button
          className={`facility-sleep-summary-panel__card facility-sleep-summary-panel__card--clickable ${activeFilter === "attention" ? "facility-sleep-summary-panel__card--active-attention" : ""}`}
          onClick={() => handleFilterClick("attention")}
          aria-label="Filter by Needs Attention"
          aria-pressed={activeFilter === "attention"}
        >
          <span className="facility-sleep-summary-panel__card-value">{counts.attention}</span>
          <div className="facility-sleep-summary-panel__card-label-row">
            <div className="facility-sleep-summary-panel__card-dot facility-sleep-summary-panel__card-dot--attention" />
            <span className="facility-sleep-summary-panel__card-label">Needs Attention</span>
          </div>
        </button>

        <button
          className={`facility-sleep-summary-panel__card facility-sleep-summary-panel__card--clickable ${activeFilter === "monitor" ? "facility-sleep-summary-panel__card--active-monitor" : ""}`}
          onClick={() => handleFilterClick("monitor")}
          aria-label="Filter by Monitor"
          aria-pressed={activeFilter === "monitor"}
        >
          <span className="facility-sleep-summary-panel__card-value">{counts.monitor}</span>
          <div className="facility-sleep-summary-panel__card-label-row">
            <div className="facility-sleep-summary-panel__card-dot facility-sleep-summary-panel__card-dot--monitor" />
            <span className="facility-sleep-summary-panel__card-label">Monitor</span>
          </div>
        </button>

        <button
          className={`facility-sleep-summary-panel__card facility-sleep-summary-panel__card--clickable ${activeFilter === "observe" ? "facility-sleep-summary-panel__card--active-observe" : ""}`}
          onClick={() => handleFilterClick("observe")}
          aria-label="Filter by Watch List"
          aria-pressed={activeFilter === "observe"}
        >
          <span className="facility-sleep-summary-panel__card-value">{counts.observe}</span>
          <div className="facility-sleep-summary-panel__card-label-row">
            <div className="facility-sleep-summary-panel__card-dot facility-sleep-summary-panel__card-dot--observe" />
            <span className="facility-sleep-summary-panel__card-label">Watch List</span>
          </div>
        </button>
      </div>

      {/* ── Metric Cards ── */}
      <div className="facility-sleep-cards-container">
        <MetricCard
          title="Sleep Time"
          icon={SleepTimeIcon}
          residents={filteredSleepTime}
          onResidentClick={handleResidentClick}
        />
        <MetricCard
          title="Efficiency"
          icon={EfficiencyIcon}
          residents={filteredEfficiency}
          onResidentClick={handleResidentClick}
        />
        <MetricCard
          title="WASO"
          icon={WasoIcon}
          residents={filteredWaso}
          onResidentClick={handleResidentClick}
        />
        <MetricCard
          title="Wake Episodes"
          icon={WakeEpisodesIcon}
          residents={filteredWakeEpisodes}
          onResidentClick={handleResidentClick}
        />
      </div>
    </main>
  );
}
